import './HelpModal.css';
import React, { useEffect } from 'react';
import {
  FaBookOpen,
  FaEnvelope,
  FaFolderOpen,
  FaWandMagicSparkles,
  FaXmark,
} from 'react-icons/fa6';
import { asIcon } from '../icons';

const CloseIcon = asIcon(FaXmark);
const BookIcon = asIcon(FaBookOpen);
const FolderIcon = asIcon(FaFolderOpen);
const MagicIcon = asIcon(FaWandMagicSparkles);
const MailIcon = asIcon(FaEnvelope);

interface HelpLabels {
  title: string;
  getStartedTitle: string;
  getStartedStep1: string;
  getStartedStep2: string;
  getStartedStep3: string;
  writingTitle: string;
  writingTip1: string;
  writingTip2: string;
  writingTip3: string;
  generationTitle: string;
  generationTip1: string;
  generationTip2: string;
  generationTip3: string;
  contactTitle: string;
  contactDescription: string;
  gotIt: string;
  version: string;
}

interface HelpModalProps {
  isOpen: boolean;
  onClose: () => void;
  labels: HelpLabels;
}

const HelpModal: React.FC<HelpModalProps> = ({ isOpen, onClose, labels }) => {
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="help-modal-backdrop" onClick={onClose}>
      <div
        className="help-modal"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="help-modal-title"
      >
        <div className="help-modal-header">
          <h2 id="help-modal-title">{labels.title}</h2>
          <button
            type="button"
            className="help-modal-close"
            onClick={onClose}
            aria-label={labels.gotIt}
          >
            <CloseIcon size={18} />
          </button>
        </div>

        <div className="help-modal-body">
          <section className="help-modal-section">
            <div className="help-modal-section-title">
              <BookIcon size={14} />
              <h3>{labels.getStartedTitle}</h3>
            </div>
            <ol>
              <li>{labels.getStartedStep1}</li>
              <li>{labels.getStartedStep2}</li>
              <li>{labels.getStartedStep3}</li>
            </ol>
          </section>

          <section className="help-modal-section">
            <div className="help-modal-section-title">
              <FolderIcon size={14} />
              <h3>{labels.writingTitle}</h3>
            </div>
            <ul>
              <li>{labels.writingTip1}</li>
              <li>{labels.writingTip2}</li>
              <li>{labels.writingTip3}</li>
            </ul>
          </section>

          <section className="help-modal-section">
            <div className="help-modal-section-title">
              <MagicIcon size={14} />
              <h3>{labels.generationTitle}</h3>
            </div>
            <ul>
              <li>{labels.generationTip1}</li>
              <li>{labels.generationTip2}</li>
              <li>{labels.generationTip3}</li>
            </ul>
          </section>

          <section className="help-modal-contact">
            <div className="help-modal-section-title neutral">
              <MailIcon size={14} />
              <h3>{labels.contactTitle}</h3>
            </div>
            <p>{labels.contactDescription}</p>
            <a href="mailto:support@scaientist.com">support@scaientist.com</a>
          </section>
        </div>

        <div className="help-modal-footer">
          <span>{labels.version}</span>
          <button type="button" onClick={onClose}>
            {labels.gotIt}
          </button>
        </div>
      </div>
    </div>
  );
};

export default HelpModal;
